import { getApiError } from '../lib/apiError';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { 
  Upload, Download, Package, Building2, FileSpreadsheet, 
  CheckCircle, AlertCircle, Loader2, FileText, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImportData = () => {
  const [productFile, setProductFile] = useState(null);
  const [supplierFile, setSupplierFile] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [productResult, setProductResult] = useState(null);
  const [supplierResult, setSupplierResult] = useState(null);

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/api/${type}/template/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_${type === 'products' ? 'produtos' : 'fornecedores'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleProductImport = async () => {
    if (!productFile) {
      toast.error('Selecione um arquivo para importar');
      return;
    }

    setLoadingProducts(true);
    setProductResult(null);

    try {
      const formData = new FormData();
      formData.append('file', productFile);

      const response = await axios.post(`${API_URL}/api/products/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProductResult(response.data);
      toast.success(response.data.message);
      setProductFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('product-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      const errorMsg = getApiError(error, 'Erro ao importar produtos');
      toast.error(errorMsg);
      setProductResult({ success: false, message: errorMsg, errors: [] });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSupplierImport = async () => {
    if (!supplierFile) {
      toast.error('Selecione um arquivo para importar');
      return;
    }

    setLoadingSuppliers(true);
    setSupplierResult(null);

    try {
      const formData = new FormData();
      formData.append('file', supplierFile);

      const response = await axios.post(`${API_URL}/api/suppliers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSupplierResult(response.data);
      toast.success(response.data.message);
      setSupplierFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('supplier-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      const errorMsg = getApiError(error, 'Erro ao importar fornecedores');
      toast.error(errorMsg);
      setSupplierResult({ success: false, message: errorMsg, errors: [] });
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const ResultAlert = ({ result }) => {
    if (!result) return null;

    return (
      <Alert className={result.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
        {result.success ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <AlertCircle className="h-5 w-5 text-red-600" />
        )}
        <AlertTitle className={result.success ? 'text-green-800' : 'text-red-800'}>
          {result.success ? 'Importação Concluída' : 'Erro na Importação'}
        </AlertTitle>
        <AlertDescription className={result.success ? 'text-green-700' : 'text-red-700'}>
          <p>{result.message}</p>
          {result.imported > 0 && (
            <p className="font-semibold mt-1">{result.imported} registro(s) importado(s)</p>
          )}
          {result.errors && result.errors.length > 0 && (
            <div className="mt-2">
              <p className="font-semibold">Erros encontrados:</p>
              <ul className="list-disc list-inside text-sm mt-1">
                {result.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="import-data-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Importação de Dados</h1>
          <p className="text-slate-500">Importe produtos e fornecedores de planilhas CSV ou Excel</p>
        </div>
      </div>

      {/* Info Card */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-5 w-5 text-blue-600" />
        <AlertTitle className="text-blue-800">Como importar dados</AlertTitle>
        <AlertDescription className="text-blue-700">
          <ol className="list-decimal list-inside space-y-1 mt-2">
            <li>Baixe o template de exemplo clicando em "Baixar Template"</li>
            <li>Preencha a planilha com seus dados (mantenha os cabeçalhos)</li>
            <li>Salve o arquivo em formato <strong>.xlsx</strong> ou <strong>.csv</strong></li>
            <li>Selecione o arquivo e clique em "Importar"</li>
          </ol>
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="products" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package size={16} />
            Produtos
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-2">
            <Building2 size={16} />
            Fornecedores
          </TabsTrigger>
        </TabsList>

        {/* Produtos Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="text-blue-600" />
                Importar Produtos
              </CardTitle>
              <CardDescription>
                Importe sua lista de produtos a partir de uma planilha Excel ou CSV
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="text-green-600" size={24} />
                  <div>
                    <p className="font-medium text-slate-800">Template de Produtos</p>
                    <p className="text-sm text-slate-500">Colunas: nome, sku, descrição, categoria, preço, custo, estoque, estoque mínimo, unidade</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => downloadTemplate('products')}
                  className="gap-2"
                  data-testid="download-products-template"
                >
                  <Download size={16} />
                  Baixar Template
                </Button>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="product-file-input">Selecione o arquivo</Label>
                  <Input
                    id="product-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setProductFile(e.target.files[0])}
                    className="cursor-pointer"
                    data-testid="product-file-input"
                  />
                  {productFile && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <FileText size={14} />
                      {productFile.name}
                    </p>
                  )}
                </div>

                <Button 
                  onClick={handleProductImport} 
                  disabled={!productFile || loadingProducts}
                  className="w-full sm:w-auto gap-2"
                  data-testid="import-products-btn"
                >
                  {loadingProducts ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Upload size={16} />
                  )}
                  {loadingProducts ? 'Importando...' : 'Importar Produtos'}
                </Button>
              </div>

              {/* Result */}
              <ResultAlert result={productResult} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fornecedores Tab */}
        <TabsContent value="suppliers">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="text-blue-600" />
                Importar Fornecedores
              </CardTitle>
              <CardDescription>
                Importe sua lista de fornecedores a partir de uma planilha Excel ou CSV
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="text-green-600" size={24} />
                  <div>
                    <p className="font-medium text-slate-800">Template de Fornecedores</p>
                    <p className="text-sm text-slate-500">Colunas: nome, cnpj, email, telefone, contato, endereço, cidade, estado</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => downloadTemplate('suppliers')}
                  className="gap-2"
                  data-testid="download-suppliers-template"
                >
                  <Download size={16} />
                  Baixar Template
                </Button>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="supplier-file-input">Selecione o arquivo</Label>
                  <Input
                    id="supplier-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setSupplierFile(e.target.files[0])}
                    className="cursor-pointer"
                    data-testid="supplier-file-input"
                  />
                  {supplierFile && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <FileText size={14} />
                      {supplierFile.name}
                    </p>
                  )}
                </div>

                <Button 
                  onClick={handleSupplierImport} 
                  disabled={!supplierFile || loadingSuppliers}
                  className="w-full sm:w-auto gap-2"
                  data-testid="import-suppliers-btn"
                >
                  {loadingSuppliers ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Upload size={16} />
                  )}
                  {loadingSuppliers ? 'Importando...' : 'Importar Fornecedores'}
                </Button>
              </div>

              {/* Result */}
              <ResultAlert result={supplierResult} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportData;
